declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

const STORAGE_KEY = 'mbbe_attribution';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const UTM_TRUNCATE = 512;
const URL_TRUNCATE = 2048;

export interface AttributionTouch {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  landing_page?: string;
  referrer?: string;
  captured_at: number;
}

export interface AttributionData {
  first_touch?: AttributionTouch;
  last_touch?: AttributionTouch;
}

function truncate(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value;
}

function readFromStorage(): AttributionData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as AttributionData;
  } catch {
    return {};
  }
}

function writeToStorage(data: AttributionData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // quota exceeded or private browsing — ignore
  }
}

function isExpired(touch: AttributionTouch): boolean {
  return Date.now() - touch.captured_at > TTL_MS;
}

function buildTouch(params: URLSearchParams): AttributionTouch | null {
  const gclid = params.get('gclid');
  const gbraid = params.get('gbraid');
  const wbraid = params.get('wbraid');
  const utm_source = params.get('utm_source');
  const utm_medium = params.get('utm_medium');
  const utm_campaign = params.get('utm_campaign');
  const utm_term = params.get('utm_term');
  const utm_content = params.get('utm_content');

  const hasAttribution = !!(gclid || gbraid || wbraid || utm_source || utm_medium || utm_campaign || utm_term || utm_content);
  if (!hasAttribution) return null;

  const touch: AttributionTouch = { captured_at: Date.now() };

  if (gclid) touch.gclid = truncate(gclid, UTM_TRUNCATE);
  if (gbraid) touch.gbraid = truncate(gbraid, UTM_TRUNCATE);
  if (wbraid) touch.wbraid = truncate(wbraid, UTM_TRUNCATE);
  if (utm_source) touch.utm_source = truncate(utm_source, UTM_TRUNCATE);
  if (utm_medium) touch.utm_medium = truncate(utm_medium, UTM_TRUNCATE);
  if (utm_campaign) touch.utm_campaign = truncate(utm_campaign, UTM_TRUNCATE);
  if (utm_term) touch.utm_term = truncate(utm_term, UTM_TRUNCATE);
  if (utm_content) touch.utm_content = truncate(utm_content, UTM_TRUNCATE);

  const landingPage = window.location.href;
  if (landingPage) touch.landing_page = truncate(landingPage, URL_TRUNCATE);

  const referrer = document.referrer;
  if (referrer) touch.referrer = truncate(referrer, URL_TRUNCATE);

  return touch;
}

/** Call once on page load. Captures first_touch if not set, always updates last_touch when attribution params are present. */
export function captureAttribution(): void {
  const params = new URLSearchParams(window.location.search);
  const touch = buildTouch(params);
  if (!touch) return;

  const stored = readFromStorage();

  const firstTouchMissing = !stored.first_touch;
  const firstTouchExpired = stored.first_touch ? isExpired(stored.first_touch) : false;

  if (firstTouchMissing || firstTouchExpired) {
    stored.first_touch = touch;
  }

  stored.last_touch = touch;
  writeToStorage(stored);
}

/** Returns live attribution data with TTL applied. Expired touches are cleared from storage and returned as undefined. */
export function getAttribution(): AttributionData {
  const stored = readFromStorage();
  let dirty = false;

  if (stored.first_touch && isExpired(stored.first_touch)) {
    delete stored.first_touch;
    dirty = true;
  }
  if (stored.last_touch && isExpired(stored.last_touch)) {
    delete stored.last_touch;
    dirty = true;
  }

  if (dirty) {
    writeToStorage(stored);
  }

  return stored;
}

export interface AttributionPayload {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  landingPage?: string;
  referrer?: string;
}

/** Returns a camelCase payload from last_touch (falling back to first_touch) matching the backend attribution schema. */
export function getCurrentAttributionPayload(): AttributionPayload {
  const { first_touch, last_touch } = getAttribution();
  const touch = last_touch ?? first_touch;
  if (!touch) return {};
  const payload: AttributionPayload = {};
  if (touch.gclid) payload.gclid = touch.gclid;
  if (touch.gbraid) payload.gbraid = touch.gbraid;
  if (touch.wbraid) payload.wbraid = touch.wbraid;
  if (touch.utm_source) payload.utmSource = touch.utm_source;
  if (touch.utm_medium) payload.utmMedium = touch.utm_medium;
  if (touch.utm_campaign) payload.utmCampaign = touch.utm_campaign;
  if (touch.utm_term) payload.utmTerm = touch.utm_term;
  if (touch.utm_content) payload.utmContent = touch.utm_content;
  if (touch.landing_page) payload.landingPage = touch.landing_page;
  if (touch.referrer) payload.referrer = touch.referrer;
  return payload;
}
